import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Alert, Typography, Spin } from "antd";
import { CheckCircleOutlined, LoadingOutlined } from "@ant-design/icons";

import {
	AssignedForm, Form as FormType, FormElement, FormSection,
	FieldResponse,
	getFormToRender,
	getFormProgress,
	saveFormProgress,
	uploadFormResults,
} from "../../../api/formApi";
import { uploadFiles } from "../../../api/file/fileServiceApi";
import { toFileResponses } from "../../../utils/forms/formFileResponses";
import { isFormDone } from "../../../utils/forms/formStatus";
import { useNotifications } from "../../../context/NotificationContext";
import UnifiedFormRenderer, { flattenFields } from "../../../components/forms/FormRenderer";
import { normaliseFieldType } from "../../../components/forms/fieldTypes";

const { Title, Paragraph, Text } = Typography;

interface PatientFormFillModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Parent refetch. Called on a successful submit, immediately before `onClose`. */
	onSave: () => void;
	assignedForm: AssignedForm;
}

/**
 * Sits in the wizard where a signature field would have been. `paragraph` renders its body
 * from `placeholder` (see FormFieldRenderer) and is excluded from `isRequired` by
 * FormRenderer, so the substitution both explains itself and cannot deadlock the wizard.
 */
const SIGNATURE_NOTICE =
	'Signature — a practitioner may not sign for a patient. Use "Hand to patient" or ' +
	"\"Open on patient's phone\" so the patient signs for themselves.";

/**
 * Replace a signature field with the notice above. `required` is cleared as well: a field
 * the practitioner is forbidden from filling must not also be a gate they cannot pass.
 */
const blockSignatureField = (field: FormElement): FormElement =>
	normaliseFieldType(field.type) === "signature"
		? {
			...field,
			type: "paragraph",
			required: false,
			placeholder: field.question ? `${field.question} — ${SIGNATURE_NOTICE}` : SIGNATURE_NOTICE,
		}
		: field;

/** Structure fed to the renderer: identical to the stored one except for signature fields. */
const blockSignatures = (
	structure: (FormElement | FormSection)[],
	useSections: boolean,
): (FormElement | FormSection)[] => {
	if (!Array.isArray(structure)) return [];
	return useSections
		? (structure as FormSection[]).map((s) => ({ ...s, fields: (s.fields ?? []).map(blockSignatureField) }))
		: (structure as FormElement[]).map(blockSignatureField);
};

/**
 * Practitioner-on-behalf fill modal for a PATIENT-typed assignment (F8, ClickUp 86d3t4ev9).
 *
 * The patient-typed sibling of `PractitionerFillFormModal`: same shape, but backed by the
 * `practitioner/forms/{render,progress,results}` trio that F3 made authenticated,
 * assignee-scoped and audited. It replaces a `window.open` to the patient's tokenised link,
 * which opened a chrome-free patient page in a new tab.
 *
 * ═══ SIGNATURES ARE NOT FILLABLE HERE ════════════════════════════════════════════════
 * A signature is the patient's own attestation; a practitioner typing into the canvas would
 * forge one. Signature fields are therefore substituted out of the structure handed to the
 * renderer, and a form carrying a REQUIRED signature refuses to submit at all — see the
 * `submissionBlocked` comment below for why refusing is safer than submitting a null.
 */
const PatientFormFillModal: React.FC<PatientFormFillModalProps> = ({
	isOpen,
	onClose,
	onSave,
	assignedForm,
}) => {
	const [formData, setFormData] = useState<FormType | null>(null);
	const [assigned, setAssigned] = useState<AssignedForm | null>(null);
	const [responses, setResponses] = useState<Record<string | number, any>>({});
	const [uploadedFiles, setUploadedFiles] = useState<Record<string | number, File[]>>({});
	const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isComplete, setIsComplete] = useState(false);
	const [loadFailed, setLoadFailed] = useState(false);
	const { showSuccess, showError } = useNotifications();

	const patientName = assignedForm.patient
		? `${assignedForm.patient.first_name ?? ""} ${assignedForm.patient.last_name ?? ""}`.trim()
		: "";

	useEffect(() => {
		if (!isOpen) return;
		let cancelled = false;

		setIsLoading(true);
		setIsComplete(false);
		setLoadFailed(false);
		setFormData(null);
		setAssigned(null);
		setResponses({});
		setUploadedFiles({});
		setCurrentSectionIndex(0);

		(async () => {
			try {
				const { form, assigned: assignment } = await getFormToRender(assignedForm.id);
				if (cancelled) return;

				// A `Reviewed` assignment is already submitted — see the note in `patient/Form.tsx`.
				// Falling through here handed the practitioner a blank form to re-key on behalf of
				// a patient who had already answered it.
				if (isFormDone(assignment.status)) {
					setIsComplete(true);
					return;
				}

				const parsedStructure = form.structure
					? (typeof form.structure === "string" ? JSON.parse(form.structure) : form.structure)
					: [];
				setFormData({ ...form, structure: parsedStructure });
				setAssigned(assignment);

				try {
					const progressData = await getFormProgress(assignedForm.id);
					if (cancelled) return;
					if (progressData?.progress) {
						const saved = typeof progressData.progress === "string"
							? JSON.parse(progressData.progress)
							: progressData.progress;
						setResponses(saved.responses || {});
						setCurrentSectionIndex(saved.currentSection || 0);
					}
				} catch {
					// No saved progress is the NORMAL first-open case and must not surface as an
					// error — only a failed *render* fetch means the form cannot be filled.
				}
			} catch {
				if (!cancelled) setLoadFailed(true);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		})();

		return () => { cancelled = true; };
	}, [isOpen, assignedForm.id]);

	const signatureFields = useMemo(() => {
		if (!formData) return [];
		return flattenFields(formData.structure, !!formData.use_sections)
			.filter((f) => normaliseFieldType(f.type) === "signature");
	}, [formData]);

	const hasSignature = signatureFields.length > 0;

	/**
	 * A REQUIRED signature makes this path unusable, and submitting anyway would be
	 * irreversible: `StoreFormResponse` flips the assignment to `Completed`, and both the
	 * patient page and this modal early-return on `Completed`. An on-behalf submit of a
	 * consent form would therefore lock the patient out of ever signing it, leaving a
	 * "completed" consent record with no consent in it. Refusing is the honest outcome.
	 */
	const submissionBlocked = signatureFields.some((f) => !!f.required);

	const renderStructure = useMemo<(FormElement | FormSection)[]>(() => {
		if (!formData) return [];
		if (!hasSignature) return formData.structure;
		return blockSignatures(formData.structure, !!formData.use_sections);
	}, [formData, hasSignature]);

	const saveProgress = async (sectionIndex: number) => {
		if (!assigned) return;
		setIsSaving(true);
		try {
			await saveFormProgress(assigned.id, JSON.stringify({
				responses,
				currentSection: sectionIndex,
				lastSaved: new Date().toISOString(),
			}));
		} catch {
			// Progress is a convenience, not the record — a failed autosave must not interrupt
			// the practitioner mid-form. The submit call is what persists the answers.
		} finally {
			setIsSaving(false);
		}
	};

	const handleSectionChange = (index: number) => {
		setCurrentSectionIndex(index);
		saveProgress(index);
	};

	const handleSubmit = async () => {
		if (!formData || !assigned || submissionBlocked) return;
		setIsSubmitting(true);
		try {
			const fieldResponses: FieldResponse[] = [];
			// The ORIGINAL structure, not the signature-substituted one: the stored response
			// must keep the form's real shape, with the signature recorded as an explicit null
			// rather than vanishing from the record.
			const allFields = flattenFields(formData.structure, !!formData.use_sections);

			for (const field of allFields) {
				const canonical = normaliseFieldType(field.type);
				if (canonical === "heading" || canonical === "paragraph") continue;

				let value = responses[field.id];

				if (canonical === "signature") {
					// Never signed on this path, whatever happens to be in state.
					value = null;
				} else if (canonical === "file") {
					const files = uploadedFiles[field.id] || [];
					value = files.length > 0
						? toFileResponses(await uploadFiles(files, assigned.patient_id), assigned.patient_id)
						: [];
				}

				// `question` only — falling back to `label` persisted the palette default
				// ("Single Select") into form_responses, and from there into the PDF export
				// and the AI prompt via QuestionnaireDataSource::formatForPrompt().
				fieldResponses.push({ id: field.id, type: field.type, question: field.question || "", response: value ?? null });
			}

			// The literal must match healthcare's `in:` rule exactly — anything else is a 422.
			await uploadFormResults(assigned.id, JSON.stringify(fieldResponses), "practitioner_on_behalf");
			showSuccess("Form submitted on behalf of the patient");
			onSave();
			onClose();
		} catch {
			showError("Failed to submit form");
		} finally {
			setIsSubmitting(false);
		}
	};

	const showCloseFooter = isComplete || loadFailed || submissionBlocked;

	return (
		<Modal
			open={isOpen}
			onCancel={onClose}
			width={800}
			destroyOnClose
			title={formData?.title || "Fill Form on Behalf"}
			footer={showCloseFooter ? <Button onClick={onClose}>Close</Button> : null}
		>
			<div data-testid="patient-form-fill-modal" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
				{isLoading ? (
					<div style={{ textAlign: "center", padding: 48 }}>
						<Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} />} />
					</div>
				) : loadFailed ? (
					<Alert
						type="error"
						showIcon
						message="This form could not be loaded"
						description="It may have been removed, or it may not be a patient form. Refresh the list and try again."
					/>
				) : isComplete ? (
					<div style={{ textAlign: "center", padding: 32 }}>
						<CheckCircleOutlined style={{ fontSize: 48, color: "var(--mg-success)" }} />
						<Title level={4} style={{ marginTop: 16 }}>Form Submitted</Title>
						<Paragraph type="secondary">This form has already been completed.</Paragraph>
					</div>
				) : formData ? (
					<>
						{/* Not decoration: F3 writes `submitted_by_role = 'practitioner'` and this
						    practitioner's user id against every answer below. They need to know
						    that before typing a patient's allergy history. */}
						<div data-testid="patient-form-fill-banner">
							<Alert
								type="warning"
								showIcon
								message={
									patientName
										? `You are completing this form on behalf of ${patientName}. Your name will be recorded against the responses.`
										: "You are completing this form on behalf of the patient. Your name will be recorded against the responses."
								}
							/>
						</div>

						{submissionBlocked ? (
							<div data-testid="patient-form-fill-signature-blocked">
								<Alert
									type="error"
									showIcon
									message="This form requires the patient's signature"
									description={
										'A practitioner must never sign for a patient, so this form cannot be submitted here. '
										+ 'Use "Hand to patient" or "Open on patient’s phone" so the patient completes and signs it themselves. '
										+ "The form is shown below for reference only."
									}
								/>
							</div>
						) : hasSignature ? (
							<div data-testid="patient-form-fill-signature-notice">
								<Alert
									type="info"
									showIcon
									message="Signature fields are disabled here"
									description={
										'This form has an optional signature, which a practitioner may not provide on a patient’s behalf. '
										+ 'Use "Hand to patient" or "Open on patient’s phone" if the signature is needed.'
									}
								/>
							</div>
						) : null}

						{isSaving && <Text type="secondary" style={{ fontSize: 12 }}>Saving…</Text>}

						{/* When blocked, `readOnly` is what removes the wizard nav and the submit
						    button entirely (FormRenderer: `isWizard = !readOnly && …`) — the refusal
						    is structural, not a disabled button someone can re-enable. */}
						<UnifiedFormRenderer
							structure={renderStructure}
							useSections={!!formData.use_sections}
							values={responses}
							onValuesChange={setResponses}
							files={uploadedFiles}
							onFilesChange={setUploadedFiles}
							readOnly={submissionBlocked}
							sectionMode="wizard"
							initialSection={currentSectionIndex}
							onSectionChange={handleSectionChange}
							onSubmit={handleSubmit}
							submitting={isSubmitting}
							submitLabel="Submit on behalf"
							submitTestId="patient-form-fill-submit"
						/>
					</>
				) : null}
			</div>
		</Modal>
	);
};

export default PatientFormFillModal;
